// @flow
import React, {useEffect} from "react";
import CartItemContainer from "../../../container/checkout/mainContentBox/CartItemContainer";
import PriceOverviewContainer from "../../../container/checkout/dataSummaryBox/PriceOverviewContainer";
import type {translate} from "../../../i18next";
import i18n from "../../../i18next";
import {toast} from "react-toastify";
import "./Cart.scss";
import PickupLocationInfoContainer from "../../../container/pickupLocation/PickupLocationInfoContainer";
import CouponContainer from "../../../container/checkout/dataSummaryBox/CouponContainer";
import {SlyCustomer, SpsCustomer} from "../../../reducers/couponReducer";
import type {HpmPrice} from "../../../helper/currency";
import {currencyConverter} from "../../../helper/currency";
import PayPalExpressButtonContainer from "../../../container/checkout/dataSummaryBox/PayPalExpressButtonContainer";
import NextButton from "../NextButton";
import usePayPalScript from "../../../hooks/usePayPalScript";
import {PAYMENT_METHODS} from "../../../models/PaymentRequests";

export type CartProps = {
    shippingText: string,
    isCartInvalid: boolean,
    withShipping: boolean,
    withMounting: boolean,
    withPickup: boolean,
    items: any,
    nextPage: () => void,
    checkCart: () => void,
    hasCashPayment: boolean,
    hasPayPalPayment: boolean,
    hasPrepaidPayment: boolean,
    hasPRZELEWY24Payment: boolean,
    hasSepaPayment: boolean,
    hasPayLaterPayment: boolean,
    hasCardPayment: boolean,
    showPickupLocation: boolean,
    t: translate,
    hasPayment: boolean,
    dealerPhone: string,
    cartVerificationError: boolean,
    paymentApiError: boolean,
    spsCustomer: ?SpsCustomer,
    slyCustomer: ?SlyCustomer,
    minOrderValue: ?HpmPrice,
    isPayPalExpressAvailable: boolean,
};

const Cart = (props: CartProps) => {
    const {
        cartVerificationError,
        paymentApiError,
        minOrderValue,
        t
    } = props;

    const {scriptResolved, isPaymentMethodAvailable} = usePayPalScript("capture");

    useEffect(() => {
        if (cartVerificationError) {
            toast(t("cart.expired"));
        }
        if (paymentApiError) {
            toast(t("cart.paymentApiError"));
        }
    }, [cartVerificationError, paymentApiError, minOrderValue, t]);

    const handleNextClicked = () => {
        props.checkCart();
        props.nextPage();
    }

    const shouldRenderPaypalPayment= (configured: boolean, paymentMethod: string) => {
        return configured && isPaymentMethodAvailable(paymentMethod);
    }

    return <div className={"hpm-cart"}>
        <div className={"hpm-cartContainer"}>
            {props.hasPayment &&
                <div className="hpm-mainContentBox">
                    {props.minOrderValue &&
                        <>
                            <div className={"hint-cart"}>
                                {props.t("cart.coupon.checkCoupon")}
                                <br/>
                                {props.t("cart.coupon.minOrderValueValue", {minOrderValue: currencyConverter(props.minOrderValue)})}
                            </div>
                        </>
                    }
                    <div className={"hpm-cartTitle"}>{props.t("cart.cartLabel")}</div>
                    <ul className="hpm-cart">
                        {props.items.map((item, index) => <CartItemContainer key={index} cartItem={item}/>)}
                    </ul>
                    <div className="hpm-box hpm-shipping">
                        <span
                            className="hpm-title">{props.t("cart.expectedDeliveryDate.expectedDeliveryDateLabel")}</span>
                        <table>
                            <tbody>
                            <tr>
                                <td>{props.shippingText}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                    {props.showPickupLocation &&
                        <div className="hpm-box">
                            <span className="hpm-title">
                                {props.withPickup ? props.t("cart.articlePickupLocation") : props.t("cart.articleMountLocation")}
                            </span>
                            <div>
                                <PickupLocationInfoContainer/>
                            </div>
                        </div>
                    }
                    {scriptResolved && <div className="hpm-box hpm-payment">
                        <span className="hpm-title">{props.t("cart.paymentMethods.availablePaymentTypes")}</span>
                        <div className={""}>
                            {shouldRenderPaypalPayment(props.hasPayPalPayment, PAYMENT_METHODS.PAYPAL) && (
                                <span title={props.t("cart.paymentMethods.paypal")} className={"hpm-paymentcarts hpm-paypal " + i18n.language.slice(0, 2)} />
                            )}
                            {shouldRenderPaypalPayment(props.hasPayLaterPayment, PAYMENT_METHODS.PAY_LATER) && (
                                <span title={props.t("cart.paymentMethods.pay-later")} className={"hpm-paymentcarts hpm-pay-later " + i18n.language.slice(0, 2)} />
                            )}
                            {shouldRenderPaypalPayment(props.hasCardPayment, PAYMENT_METHODS.CARD) && (
                                <span title={props.t("cart.paymentMethods.card")} className={"hpm-paymentcarts hpm-card " + i18n.language.slice(0, 2)} />
                            )}
                            {shouldRenderPaypalPayment(props.hasPRZELEWY24Payment, PAYMENT_METHODS.PRZELEWY24) && (
                                <span title={props.t("cart.paymentMethods.przelewy24")} className={"hpm-paymentcarts hpm-przelewy24 " + i18n.language.slice(0, 2)} />
                            )}
                            {props.hasPrepaidPayment && (
                                <span title={props.t("cart.paymentMethods.prepaid")} className={"hpm-paymentcarts hpm-bankWire " + i18n.language.slice(0, 2)} />
                            )}
                            {!props.withShipping && props.hasCashPayment && (
                                <span title={props.t("cart.paymentMethods.cash")} className={"hpm-paymentcarts hpm-euro " + i18n.language.slice(0, 2)} />
                            )}
                            {shouldRenderPaypalPayment(props.hasSepaPayment, PAYMENT_METHODS.SEPA) && (
                                <span title={props.t("cart.paymentMethods.sepa")} className={"hpm-paymentcarts hpm-sepa " + i18n.language.slice(0, 2)} />
                            )}
                        </div>
                    </div>
                    }
                </div>
            }
            {!props.hasPayment &&
                <div className="hpm-cartErrorContent">
                    <div className="hpm-cartErrorSpacer"></div>
                    <div className="hpm-cartErrorMessage">
                        {props.t("cart.error.infoTextPartOne")}
                    </div>
                    {(props.dealerPhone && props.dealerPhone !== "0") &&
                        <div className="hpm-cartErrorMessage">
                            {props.t("cart.error.infoTextPartTwo", {count: props.dealerPhone})}
                        </div>
                    }
                    <div className="hpm-cartErrorMessage">
                        {props.t("cart.error.code")}
                    </div>
                </div>
            }
            <div className="hpm-dataSummaryBox">
                {!props.slyCustomer && !props.spsCustomer && <CouponContainer t={props.t}/>}
                <div className={"hpm-dataSummaryBoxContent"}>
                    <PriceOverviewContainer />
                    <div className={"hpm-dataSummaryBoxButtons"}>
                        {props.isCartInvalid &&
                            <span>
                                <small className={"hpm-error"}>{props.t("cart.invalidMsg")}</small>
                            </span>
                        }
                        <NextButton
                            onClick={handleNextClicked}
                            disabled={props.isCartInvalid || !props.hasPayment || props.minOrderValue}
                        >
                            {props.t("cart.calculation.toCheckOut")}
                        </NextButton>
                        {props.isPayPalExpressAvailable && <PayPalExpressButtonContainer />}
                    </div>
                </div>
            </div>
        </div>
    </div>;
};

export default Cart;
